import { ChangeEvent, Dispatch, SetStateAction, useRef, useState } from 'react';

import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { UploadedFile } from '@components/file-field';
import { FileInputContent } from '@components/file-field/file-input-content';
import { createForm, useCreateFormStore } from '@components/form';
import { IconDynamicField } from '@components/icons/dynamic-field';
import { IconSend } from '@components/icons/send';
import { LabelFieldTable } from '@components/label-field-table/label-field-table';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useDocumentTemplatePreview } from 'api/documents/hooks';
import { DocumentTemplate, DocumentTemplateField } from 'api/documents/requests';

import { TYPE_ICON_MAP } from '../common';

export const usePreviewVariablesModal = () => useModal(PreviewVariablesModal, { variant: 'LARGE' });

type ExampleVariableModalProps = {
  document: DocumentTemplate;
};

type FormData = Record<string, string | File>;
const Form = createForm<FormData>();

const PreviewVariablesModal = ({ document }: ExampleVariableModalProps) => {
  const { t } = useScopedTranslate(locale);
  const showDocumentPreview = useDocumentTemplatePreview(document.shortId);
  const formStore = useCreateFormStore();
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File | null>>({});

  const handleSubmit = () => {
    const dateFieldIds = new Set(
      document.fieldConfig.filter((field) => field.type === 'date').map((field) => field.id)
    );
    const data = formStore.getState().values;

    const allData = { ...data };
    Object.entries(uploadedFiles).forEach(([key, file]) => {
      allData[key] = file;
    });

    const processedData: Record<string, string> = {};
    const filesToProcess: Array<{ key: string; file: File }> = [];

    Object.entries(allData).forEach(([key, value]) => {
      if (value instanceof File) {
        filesToProcess.push({ key, file: value });
      } else if (value instanceof Date) {
        processedData[key] = value.toISOString();
      } else if (dateFieldIds.has(key) && value != null) {
        const parsed = new Date(String(value));
        processedData[key] = isNaN(parsed.getTime()) ? String(value) : parsed.toISOString();
      } else {
        processedData[key] = String(value || '');
      }
    });

    if (filesToProcess.length === 0) {
      showDocumentPreview.mutate(processedData);
      return;
    }

    let filesProcessed = 0;
    filesToProcess.forEach(({ key, file }) => {
      const reader = new FileReader();
      reader.onload = () => {
        processedData[key] = reader.result as string;
        filesProcessed++;

        if (filesProcessed === filesToProcess.length) {
          showDocumentPreview.mutate(processedData);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Stack space={6}>
          <Text text={t('instructions')} font="regular14" color="textSecondary" />
          <Form onSubmit={handleSubmit} store={formStore}>
            <LabelFieldTable>
              <LabelFieldTable.HeaderRow>
                <LabelFieldTable.HeaderColumn label={t('variable')} icon={IconDynamicField} />
                <LabelFieldTable.HeaderColumn label={t('exampleValue')} icon={IconSend} />
              </LabelFieldTable.HeaderRow>
              <tbody>
                {document.fieldConfig.map((field) => (
                  <VariableRow
                    key={field.id}
                    field={field}
                    document={document}
                    setUploadedFiles={setUploadedFiles}
                  />
                ))}
              </tbody>
            </LabelFieldTable>
          </Form>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <CancelSaveButtons
          alwaysShow
          onCancel={hideModal}
          onSave={handleSubmit}
          isDisabled={showDocumentPreview.isLoading}
          translationsOverride={{ save: t('preview') }}
        />
      </Modal.Footer>
    </Modal>
  );
};

type VariableRowProps = ExampleVariableModalProps & {
  field: DocumentTemplateField;
  setUploadedFiles: Dispatch<SetStateAction<Record<string, File | null>>>;
};

const VariableRow = ({ field, document, setUploadedFiles }: VariableRowProps) => {
  const Icon = TYPE_ICON_MAP[field.type] ?? TYPE_ICON_MAP.text;
  const defaultValue = document.exampleData?.[field.id] || document.defaultData?.[field.id];

  return (
    <LabelFieldTable.Row id={field.id} icon={Icon} label={field.label}>
      {field.type === 'select' ? (
        <Form.Select
          id={field.id}
          label={field.label}
          hideLabel
          defaultValue={defaultValue}
          options={field.options ?? []}
          isClearable={false}
        />
      ) : field.type === 'file' ? (
        <FileInput
          onFileChange={(file) => {
            setUploadedFiles((prev) => ({ ...prev, [field.id]: file }));
          }}
        />
      ) : field.type === 'date' ? (
        <Form.DatePicker
          id={field.id}
          label={field.label}
          hideLabel
          defaultValue={defaultValue}
          placeholder={field.label}
        />
      ) : ['number', 'decimal'].includes(field.type) ? (
        <Form.NumberField
          id={field.id}
          label={field.label}
          hideLabel
          defaultValue={defaultValue}
          placeholder={field.label}
          isDecimal={field.type === 'decimal'}
        />
      ) : (
        <Form.TextField
          id={field.id}
          label={field.label}
          hideLabel
          defaultValue={defaultValue}
          placeholder={field.label}
        />
      )}
    </LabelFieldTable.Row>
  );
};

const FileInput = ({ onFileChange }: { onFileChange: (file: File | null) => void }) => {
  const { t } = useScopedTranslate(locale);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      onFileChange(file);
    }
  };

  return (
    <Stack space={2}>
      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        ref={fileInputRef}
      />
      {selectedFile ? (
        <UploadedFile
          name={selectedFile.name}
          onRemove={() => {
            setSelectedFile(null);
            onFileChange(null);
          }}
          variant="small"
        />
      ) : (
        <FileInputContent onClick={() => fileInputRef.current?.click()} placeholder={t('upload')} />
      )}
    </Stack>
  );
};

const locale = {
  en: {
    title: 'Preview document variables',
    preview: 'Preview',
    variable: 'Variable',
    exampleValue: 'Preview value',
    instructions:
      'Before previewing your document, add some test values to see how the document will look with data',
    upload: 'Upload',
    selected: 'Selected: {fileName}',
  },
};
